import { NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import mapboxgl, { GeoJSONSource, MapMouseEvent } from 'mapbox-gl';
import { Subscription } from 'rxjs';

import {
  FilterMethod,
  MapFilterType
} from '@core/models/data-visualization/data-visualization.state';
import { VisualFocusService } from '@core/services/visual-focus/visual-focus.service';
import { setFilterOnAggregator } from '@core/store/data-visualization/data-visualization.actions';
import { isNil } from '@core/utils/shared-utils';

import { IconMapGalia } from '@features/components/visualization/mapbox/icon-map-galia';
import { MapElement } from '@features/components/visualization/mapbox/map-elements/map-element';
import { MapSource } from '@features/components/visualization/mapbox/map-elements/map-source';
import { MapEvents } from '@features/components/visualization/mapbox/map-events';
import { MapboxTooltip } from '@features/components/visualization/mapbox/mapbox-tooltip';
import { GaliaMarker } from '@features/components/visualization/mapbox/models/galia-marker';
import {
  GeoLocation,
  HighlightAggregatorMarker
} from '@features/components/visualization/mapbox/models/map-data';
import {
  MapFocusService,
  UNLIT_DOT_OPACITY,
  UNLIT_DOT_RADIUS,
  UNLIT_MARKER_COLOR
} from '@features/components/visualization/mapbox/services/focus.service';
import { MapRegistryService } from '@features/components/visualization/mapbox/services/registry.service';

export class MapMarkers implements MapElement {
  private readonly layerDefault: string;
  private readonly layerSelected: string;
  private readonly layerUnselected: string;
  private readonly layerInvisible: string;
  private readonly layerClusterCount: string;
  private readonly layerUnclustered: string;
  private readonly layerUnclusteredInvisible: string;
  private readonly layerUnclusteredSpiderfy: string;
  private readonly layerCluster: string;
  private readonly layerDim: string;
  private readonly layerSequence: string;
  private readonly clusterMaxZoom: number = 16;
  private readonly source: MapSource;
  private readonly clusterSource: MapSource;
  private readonly spiderfySource: MapSource;
  private dataMarkers: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection' as const,
    features: []
  };
  private groupingEnabled: boolean = true;
  private hoveredMarkerStateId: string | undefined;
  /**
   * Where the pointer last was over the canvas, `null` once it has left.
   *
   * Mapbox derives its layer-scoped `mouseenter` / `mouseleave` from pointer movement alone: it
   * never re-tests what lies under a *stationary* pointer when the camera moves. So a stop that
   * slides out from under a resting cursor — a wheel zoom, a `+` click, a `fitBounds` — fires no
   * leave event, and the tooltip it opened describes a stop that is no longer there. Keeping the
   * last position is what lets `dropStaleHover` re-test it when the camera settles.
   */
  private pointerPoint: mapboxgl.Point | null = null;
  private selectedHighlightAggregator: HighlightAggregatorMarker | undefined;
  private subscription: Subscription | undefined;
  private eventCleanupFunctions: Array<() => void> = [];
  private offsetCache = new Map<string, Array<[number, number]>>();
  private readonly maxCacheSize = 50;

  constructor(
    private readonly map: mapboxgl.Map,
    private readonly tooltip: MapboxTooltip,
    private readonly store: Store,
    private readonly registry: MapRegistryService,
    private readonly zone: NgZone,
    private readonly focus: VisualFocusService,
    private readonly focusPainter: MapFocusService
  ) {
    const layer = this.registry.getMarkerLayers();
    this.layerDefault = layer.default;
    this.layerSelected = layer.selected;
    this.layerUnselected = layer.unselected;
    this.layerInvisible = layer.invisible;
    this.layerCluster = layer.clustered;
    this.layerUnclustered = layer.unclustered;
    this.layerUnclusteredInvisible = layer.unclusteredInvisible;
    this.layerUnclusteredSpiderfy = layer.unclusteredSpiderfy;
    this.layerClusterCount = layer.clusteredCount;
    this.layerDim = layer.dim;
    this.layerSequence = layer.sequence;

    const source = this.registry.getMarkerSources();
    this.source = new MapSource(source.default, this.map);
    this.clusterSource = new MapSource(source.clustered, this.map);
    this.spiderfySource = new MapSource(source.spiderfy, this.map);
  }

  destroy(): void {
    this.subscription?.unsubscribe();

    this.eventCleanupFunctions.forEach((cleanup) => cleanup());
    this.eventCleanupFunctions = [];
    // No `mouseleave` reaches a handler that has just been unbound, so a pointer resting on a
    // stop as the map goes away would leave the hover raised for good.
    this.focus.clearHover('action');

    this.source.cleanup();
    this.clusterSource.cleanup();
    this.spiderfySource.cleanup();

    this.dataMarkers = { type: 'FeatureCollection', features: [] };
    this.hoveredMarkerStateId = undefined;
  }

  init(): void {
    this.initSource();
    this.initLayer();
    // Before `MapFocusService.attach`, which reads it when it installs its expressions.
    this.focusPainter.setGrouping(this.groupingEnabled);
    this.subscription = MapEvents.getInstance().on<GaliaMarker[]>(
      'update-markers',
      this.updateMarkers.bind(this)
    );
  }

  registerEvents(): void {
    const onMouseEnter = (e: MapMouseEvent) => {
      const feature = e.features?.at(0);
      if (feature?.geometry.type === 'Point') {
        const point = feature.geometry as GeoJSON.Point;

        if (feature.properties?.['icon-offset']) {
          const offset = JSON.parse(feature.properties['icon-offset']) as [number, number];
          const pixel = this.map.project(point.coordinates as [number, number]);
          const adjustedPixel: [number, number] = [
            pixel.x + offset[0] * 0.6,
            pixel.y + offset[1] * 0.6
          ];
          point.coordinates = this.map.unproject(adjustedPixel).toArray();
        }

        this.tooltip.showTooltip(
          e.lngLat.lng,
          point.coordinates,
          feature?.properties?.['description']
        );
      }

      if (e.features && e.features.length > 0) this.changeToHoverState(e.features[0]);
    };

    const mouseLayers = this.hoverLayerNames;

    this.map.on('mouseenter', mouseLayers, onMouseEnter);
    this.eventCleanupFunctions.push(() => {
      this.map.off('mouseenter', mouseLayers, onMouseEnter);
    });

    const onMouseMove = () => {
      this.changeToNotHoverState(this.hoveredMarkerStateId!);
    };
    this.map.on('mouseleave', mouseLayers, onMouseMove);
    this.eventCleanupFunctions.push(() => {
      this.map.off('mouseleave', mouseLayers, onMouseMove);
    });

    const onTouch = (e: MapMouseEvent) => this.centerMapOnCluster(e);
    this.map.on('click', [this.layerCluster], onTouch);
    this.eventCleanupFunctions.push(() => {
      this.map.off('click', [this.layerCluster], onTouch);
    });

    const deletePointsFn = () => this.deletePointsSpiderfy();
    this.map.on('click', deletePointsFn);
    this.map.on('zoomstart', deletePointsFn);
    this.map.on('touchstart', deletePointsFn);

    this.eventCleanupFunctions.push(() => {
      this.map.off('click', deletePointsFn);
      this.map.off('zoomstart', deletePointsFn);
      this.map.off('touchstart', deletePointsFn);
    });

    this.zone.runOutsideAngular(() => {
      const onFocusMove = (e: MapMouseEvent) => {
        const uid = (e.features?.at(0)?.properties?.['id'] as string | undefined) ?? null;
        if (uid !== null && this.focus.coversAction(uid)) return;
        this.zone.run(() => this.focus.hoverAction(uid));
      };
      this.map.on('mousemove', mouseLayers, onFocusMove);
      this.eventCleanupFunctions.push(() => {
        this.map.off('mousemove', mouseLayers, onFocusMove);
      });

      const onFocusLeave = () => {
        if (this.focus.currentHoverTarget()?.kind !== 'action') return;
        this.zone.run(() => this.focus.hoverAction(null));
      };
      this.map.on('mouseleave', mouseLayers, onFocusLeave);
      this.eventCleanupFunctions.push(() => {
        this.map.off('mouseleave', mouseLayers, onFocusLeave);
      });

      // Unscoped, so it follows the pointer across the whole canvas rather than only over
      // stops. Registered after the layer-scoped `mousemove` above, which is the one that has
      // to be found first by anything looking up a handler by event name. Writing a field costs
      // nothing and needs no change detection, hence no `zone.run` — `dropStaleHover` enters
      // the zone itself, for the writes that do.
      const onPointerMove = (e: MapMouseEvent) => {
        this.pointerPoint = e.point;
      };
      this.map.on('mousemove', onPointerMove);

      // A pointer that leaves the canvas takes any hover with it. Mapbox raises `mouseleave` on
      // the layer here too, but only when the pointer walks off a stop on its way out — leaving
      // through a stop that sits against the canvas edge does not qualify.
      const onPointerOut = () => {
        this.pointerPoint = null;
        this.dropStaleHover();
      };
      this.map.on('mouseout', onPointerOut);

      const onCameraSettled = () => this.dropStaleHover();
      this.map.on('moveend', onCameraSettled);

      this.eventCleanupFunctions.push(() => {
        this.map.off('mousemove', onPointerMove);
        this.map.off('mouseout', onPointerOut);
        this.map.off('moveend', onCameraSettled);
      });

      const onFocusClick = (e: MapMouseEvent) => {
        const uid = e.features?.at(0)?.properties?.['id'] as string | undefined;
        if (!uid) return;
        this.zone.run(() => this.focus.toggleJobPin(uid));
      };
      this.map.on('click', mouseLayers, onFocusClick);
      this.eventCleanupFunctions.push(() => {
        this.map.off('click', mouseLayers, onFocusClick);
      });
    });
  }

  /**
   * Enable or disable marker grouping (clustering).
   *
   * @param {boolean} enabled - True to enable grouping, false to disable.
   * @param {GaliaMarker[]} markers - List of markers to update.
   */
  changeGroupingMode(enabled: boolean, markers: GaliaMarker[]): void {
    this.groupingEnabled = enabled;
    this.focusPainter.setGrouping(enabled);

    const map = this.map;
    function setVisibilityIfChanged(layerId: string, visibility: 'visible' | 'none') {
      const currentVisibility = map.getLayoutProperty(layerId, 'visibility');
      if (currentVisibility !== visibility) {
        map.setLayoutProperty(layerId, 'visibility', visibility);
      }
    }

    setVisibilityIfChanged(this.layerCluster, enabled ? 'visible' : 'none');
    setVisibilityIfChanged(this.layerUnclusteredInvisible, enabled ? 'visible' : 'none');
    setVisibilityIfChanged(this.layerUnclustered, enabled ? 'visible' : 'none');
    setVisibilityIfChanged(this.layerClusterCount, enabled ? 'visible' : 'none');
    // The coloured `default` layer stays on the map in both modes. Grouped, it is the only
    // layer that can draw a focused route's own stops: a stop inside a cluster is drawn by no
    // other layer, and the focus takes the clusters away. `MapFocusService` holds it at zero
    // for every stop no focus has named, so the grouped view is unchanged until one is.
    setVisibilityIfChanged(this.layerDefault, 'visible');
    setVisibilityIfChanged(this.layerInvisible, enabled ? 'none' : 'visible');
    // The numbers stay on the map in both modes, like the layer above and for the same reason:
    // they are drawn from the unclustered source, the only one holding every stop.

    this.updateMarkers(markers);
  }

  updateMarkers(markers: GaliaMarker[]) {
    const features = this.createMapboxMarkers(markers);

    this.source.updateSourceOnReady(features);
    this.clusterSource.updateSourceOnReady(features);
  }

  private centerMapOnCluster(evt: MapMouseEvent) {
    // A focus takes the clusters off the map, and an invisible bubble must not go on claiming
    // clicks: zero opacity hides a feature from the eye, never from `queryRenderedFeatures`.
    // Without this, clicking what looks like empty ground next to a focused route zooms the
    // map into a cluster nobody can see.
    if (!isNil(this.focus.focus())) return;

    const features = this.map.queryRenderedFeatures(evt.point, {
      layers: [this.layerCluster]
    });

    const source = this.map.getSource(this.clusterSource.name) as GeoJSONSource;
    const currentZoom = this.map.getZoom();
    if (features && features.length > 1) {
      // Fit map bound in clusters bounds
      const coordinates = features.map((f) => f.geometry as GeoJSON.Point);
      const bounds = coordinates.reduce(
        (b, coord) => {
          return b.extend(coord.coordinates as [number, number]);
        },
        new mapboxgl.LngLatBounds(
          coordinates[0].coordinates as [number, number],
          coordinates[0].coordinates as [number, number]
        )
      );
      this.map.fitBounds(bounds, { padding: 200 });
      return;
    }

    if (features[0]) {
      const clusterId = features[0].properties!['cluster_id'];
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        if (!zoom) return;

        const center =
          features[0].geometry.type === 'Point'
            ? (features[0].geometry.coordinates as [number, number])
            : undefined;
        if (currentZoom >= this.clusterMaxZoom) {
          this.spiderfyCluster(source, clusterId, center!);
          this.map.easeTo({
            center: center
          });
        } else {
          this.map.easeTo({
            center: center,
            zoom: this.clusterMaxZoom
          });
        }
      });
    }
  }

  /**
   * Expands a cluster into its individual markers by slightly offsetting them in a circular
   * pattern.
   *
   * @private
   * @param {mapboxgl.GeoJSONSource} source
   * @param {number} clusterId
   * @param {mapboxgl.LngLat} center
   */
  private spiderfyCluster(
    source: GeoJSONSource,
    clusterId: number,
    center: [number, number]
  ) {
    if (!this.groupingEnabled) return;

    source.getClusterLeaves(clusterId, Infinity, 0, (error, features) => {
      if (error) return;
      if (!features) return;
      if (features.length > 0) {
        const spiderfiedPositions =
          features.length > 10
            ? this.calculateSpiderfiedPositions(features.length)
            : this.calculateSpiderfiedPositionsCircle(features.length);

        const geoJson: GeoJSON.GeoJSON = {
          type: 'FeatureCollection',
          features: features.map((feature, index) => ({
            ...feature,
            properties: {
              ...feature.properties,
              'icon-offset': spiderfiedPositions[index]
            },
            geometry: {
              ...feature.geometry,
              type: 'Point',
              coordinates: center
            }
          }))
        };

        const spiderfySource = this.map.getSource(
          this.spiderfySource.name
        ) as GeoJSONSource;
        spiderfySource.setData(geoJson);

        this.map.setLayoutProperty(this.layerClusterCount, 'visibility', 'none');
        this.map.setLayoutProperty(this.layerUnclusteredSpiderfy, 'visibility', 'visible');
        // The dimming these layers take while the spider is open is an opacity that
        // `MapFocusService` owns. Written here it would erase a painted focus, and be erased by
        // that service's next repaint.
        this.focusPainter.setSpiderfyOpen(true);
      }
    });
  }

  /**
   * Calculates positions in a circular pattern around a center point for spiderfying
   * markers.
   *
   * @private
   * @param {number} count - Number of markers to position
   * @returns {number[][]} Array of [x, y] offsets for each marker
   */
  private calculateSpiderfiedPositionsCircle(count: number): number[][] {
    const leavesSeparation = 80;
    const leavesOffset = [0, 0];
    const points = [];
    const theta = (2 * Math.PI) / count;
    let angle: number;

    for (let i = 0; i < count; i += 1) {
      angle = theta * i;
      const x = leavesSeparation * Math.cos(angle) + leavesOffset[0];
      const y = leavesSeparation * Math.sin(angle) + leavesOffset[1];
      points.push([x, y]);
    }
    return points;
  }

  /**
   * Calculates the spiderfied position for a marker based on an expanding spiral pattern.
   *
   * @private
   * @param {number} count - Total number of markers
   * @returns {number[][]} Array of [x, y] offsets for each marker
   */
  private calculateSpiderfiedPositions(count: number): number[][] {
    const legLengthStart = 30;
    const legLengthFactor = 9;
    const leavesSeparation = 55;

    const leavesOffset = [0, 0];
    const points = [];
    let legLength = legLengthStart;
    let angle = 0;

    for (let i = 0; i < count; i++) {
      angle += leavesSeparation / legLength + i * 0.0005;
      const x = legLength * Math.cos(angle) + leavesOffset[0];
      const y = legLength * Math.sin(angle) + leavesOffset[1];
      points.push([x, y]);

      legLength += (Math.PI * 2 * legLengthFactor) / angle;
    }
    return points;
  }

  /**
   * Removes spiderfied markers and restores the original clustered view.
   *
   * @private
   */
  private deletePointsSpiderfy() {
    // Reported before the grouping guard: a filter applied while a spider is open switches
    // grouping off first, and leaving the painter believing a spider is still open holds the
    // cluster and unclustered icons at the backdrop opacity for the rest of the map's life.
    this.focusPainter.setSpiderfyOpen(false);

    // The spider's own layer comes down whatever grouping now says, and before the guard below
    // for the same reason the line above sits there: a filter applied while a spider is open
    // switches grouping off first, so by the time this runs the guard would skip the reset and
    // leave the layer visible over stale data — a ring of ghost pins around a cluster centre
    // that is no longer on the map.
    const spiderOpen =
      this.map.getLayoutProperty(this.layerUnclusteredSpiderfy, 'visibility') === 'visible';
    if (spiderOpen) {
      this.map.setLayoutProperty(this.layerUnclusteredSpiderfy, 'visibility', 'none');

      const spiderfySource = this.map.getSource(this.spiderfySource.name) as GeoJSONSource;
      spiderfySource?.setData({
        type: 'FeatureCollection',
        features: []
      });
    }

    if (!this.groupingEnabled) return;
    this.map.setLayoutProperty(this.layerClusterCount, 'visibility', 'visible');
  }

  /**
   * Hover lives in `hoveredMarkerStateId`, never in mapbox's feature state. No marker layer
   * paints a `hover` state — the focus tier `MapFocusService` writes is the only state these
   * sources carry — so a hover written there would re-evaluate every stop's paint arrays on
   * each pointer move and change nothing on screen.
   */
  private changeToHoverState(features: mapboxgl.GeoJSONFeature) {
    this.map.getCanvas().style.cursor = 'pointer';
    if (!isNil(features) && features.id) {
      if (this.hoveredMarkerStateId === features.id) {
        return;
      }

      this.hoveredMarkerStateId = features.id as string;

      try {
        const stringHighlightAggregatorId: string =
          features.properties!['highlightAggregatorIds'];

        if (!stringHighlightAggregatorId) {
          return;
        }

        const highlightAggregatorId: string[] = JSON.parse(stringHighlightAggregatorId);

        if (highlightAggregatorId && highlightAggregatorId.length > 0) {
          this.selectedHighlightAggregator = {
            aggregatorIds: highlightAggregatorId,
            markerSelectedId: features.properties!['id']
          };

          this.store.dispatch(
            setFilterOnAggregator({
              filteredAggregatorIds: this.selectedHighlightAggregator!.aggregatorIds,
              filterMethod: FilterMethod.BY_HIGHLIGHT_MARKER,
              markerId: this.selectedHighlightAggregator!.markerSelectedId
            })
          );
        }
      } catch (e) {
        /* empty */
        console.error(e);
      }
    }
  }

  /**
   * Calculate a circle offset for markers that are at the same coordinates to avoid
   * overlap.
   *
   * @private
   * @param index Index of the marker among those at the same location (0-based)
   * @param count Total number of markers at the same location
   * @param originalCoords Original coordinates of the markers
   * @returns A tuple [xOffset, yOffset] representing the offset in longitude and latitude
   */
  private calculateCircleOffset(
    index: number,
    count: number,
    originalCoords: GeoLocation
  ): [number, number] {
    const cacheKey = `${originalCoords.lat.toFixed(6)}_${count}`;

    if (!this.offsetCache.has(cacheKey)) {
      const circleSeparation = 0.025;
      const latRad = originalCoords.lat * (Math.PI / 180);
      const cosLat = Math.cos(latRad);

      const radiusLng = circleSeparation / (111.319 * cosLat);
      const radiusLat = circleSeparation / 110.574;

      const offsets: Array<[number, number]> = [];
      for (let i = 0; i < count; i++) {
        const angle = (2 * Math.PI * i) / count;
        const offsetLng = radiusLng * Math.cos(angle);
        const offsetLat = radiusLat * Math.sin(angle);
        offsets.push([offsetLng, offsetLat]);
      }

      this.offsetCache.set(cacheKey, offsets);
    }

    return this.offsetCache.get(cacheKey)![index] || [0, 0];
  }

  /**
   * Groups markers by their coordinates
   *
   * @param markers Marker list
   * @returns Map where the key is "lng,lat" and the value is the list of markers at those
   *   coordinates
   */
  private groupMarkersByCoordinates(markers: GaliaMarker[]): Map<string, GaliaMarker[]> {
    const groups = new Map<string, GaliaMarker[]>();

    markers.forEach((marker) => {
      const key = coordKey(marker.coords, IDENTICAL_COORD_PRECISION);

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(marker);
    });

    return groups;
  }

  /**
   * Applies offsets to markers that overlap (same coordinates) to avoid visual overlap.
   *
   * @param markers Original marker list
   * @returns New marker list with adjusted coordinates for overlapping markers
   */
  private applyOffsetToOverlappingMarkers(markers: GaliaMarker[]): GaliaMarker[] {
    const groups = this.groupMarkersByCoordinates(markers);
    const adjustedMarkers: GaliaMarker[] = [];

    groups.forEach((group) => {
      if (group.length === 1) {
        adjustedMarkers.push(group[0]);
      } else {
        group.forEach((marker, index) => {
          const [offsetLng, offsetLat] = this.calculateCircleOffset(
            index,
            group.length,
            marker.coords
          );

          adjustedMarkers.push({
            ...marker,
            coords: {
              lng: marker.coords.lng + offsetLng,
              lat: marker.coords.lat + offsetLat
            }
          });
        });
      }
    });

    return adjustedMarkers;
  }

  /**
   * The layers whose features a hover can land on.
   *
   * Handlers are bound against these names unfiltered, so a layer the style does not carry yet
   * still gets one. `queryRenderedFeatures` is the opposite — it throws on a layer the style
   * lacks — hence the filtered read in `dropStaleHover`.
   */
  private get hoverLayerNames(): string[] {
    return [
      this.layerInvisible,
      this.layerUnclusteredInvisible,
      this.layerUnclusteredSpiderfy
    ];
  }

  /**
   * Closes a hover whose stop is no longer under the pointer.
   *
   * Called where mapbox raises no leave event of its own — see `pointerPoint`. It tests for the
   * *same* stop rather than for any stop: a camera move can leave a different one under the
   * pointer, and carrying on with the old tooltip over it would describe the wrong stop. Nothing
   * re-opens a tooltip for the new one, because the pointer has not moved and `mouseenter` needs
   * it to; a closed tooltip is the right answer either way, since the box is positioned once at
   * open time and cannot follow the camera.
   */
  private dropStaleHover(): void {
    const hovered = this.hoveredMarkerStateId;
    if (isNil(hovered)) return;

    const layers = this.hoverLayerNames.filter((layer) => this.map.getLayer(layer));
    const stillUnderPointer =
      !isNil(this.pointerPoint) &&
      layers.length > 0 &&
      this.map
        .queryRenderedFeatures(this.pointerPoint, { layers })
        .some((feature) => feature.properties?.['id'] === hovered);

    if (stillUnderPointer) return;

    this.zone.run(() => {
      this.changeToNotHoverState(hovered);
      // `changeToNotHoverState` predates the linked focus and does not know about it, so the
      // action hover is cleared here or it stays raised on a stop nobody is pointing at.
      if (this.focus.currentHoverTarget()?.kind === 'action') this.focus.hoverAction(null);
    });
  }

  private changeToNotHoverState(id: string) {
    if (id != null) {
      this.map.getCanvas().style.cursor = '';
      this.hoveredMarkerStateId = undefined;
    }

    if (
      this.selectedHighlightAggregator &&
      this.selectedHighlightAggregator.aggregatorIds.length > 0
    ) {
      this.selectedHighlightAggregator.aggregatorIds = [MapFilterType.RESET];
      this.store.dispatch(
        setFilterOnAggregator({
          filteredAggregatorIds: this.selectedHighlightAggregator.aggregatorIds,
          filterMethod: FilterMethod.DISABLED
        })
      );
    }

    this.tooltip.startCloseTimeout();
  }

  private createMapboxMarkers(markers: GaliaMarker[]): GeoJSON.FeatureCollection {
    const geoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: []
    };
    if (markers.length === 0) {
      return geoJson;
    }

    // Both counted and labelled before the offsets are applied: that pass regroups the array by
    // coordinate, and two stops standing on the same site — a route's opening and closing depot,
    // a pickup and its delivery — are exactly the pair it pulls together, which would renumber
    // everything after them. See `sequenceLabels` for why the labels want the same coordinates
    // the sites have rather than the ones the fan leaves behind.
    const sequences = stopSequences(markers);
    const labels = sequenceLabels(markers, sequences);

    const adjustedMarkers = this.groupingEnabled
      ? markers
      : this.applyOffsetToOverlappingMarkers(markers);

    adjustedMarkers.forEach((marker) => {
      const type = 'Feature' as const;
      const id = marker.uid;
      const geometry = {
        type: 'Point' as const,
        coordinates: [marker.coords.lng, marker.coords.lat]
      };

      const color = marker.color ?? '#4e4c4c';
      const markerType = marker.type ?? 'basic';
      const iconName = `${markerType}-${color.replace('#', '')}`;

      // Ensure icon exists before using it (lazy generation)
      IconMapGalia.ensureIconExists(this.map, markerType, color);

      const properties = {
        id: id,
        color: color,
        description: marker.description,
        icon: iconName,
        sequence: labels.get(marker.uid) ?? '',
        structuralAggregatorIds: (marker.structuralAggregatorIds ?? []).map((id) =>
          id.toString()
        ),
        highlightAggregatorIds: (marker.highlightAggregatorIds ?? []).map((id) =>
          id.toString()
        ),
        draggable: marker.draggable ?? false
      };
      const feature = {
        type: type,
        id: id,
        geometry: geometry,
        properties: properties
      };
      geoJson.features.push(feature as never);
    });
    return geoJson;
  }

  /**
   * The bottom-most route layer, or `undefined` on a map that carries no routes.
   *
   * The unlit stops are added *under* it, so a dot never lands on the one line the focus exists
   * to surface. Lit stops stay above the routes, where a pin belongs.
   */
  private belowRoutes(): string | undefined {
    const casing = this.registry.getRouteLayers().casing;
    return this.map.getLayer(casing) ? casing : undefined;
  }

  private initLayer() {
    const under = this.belowRoutes();
    // `icon-ignore-placement` follows from `icon-allow-overlap`: an icon that can never be
    // culled has no business culling anything else. Without it these layers reserve a box in
    // the collision index for every stop the map has — including the stops of the `default`
    // layer, which is held at zero opacity in grouped mode and placed all the same — and the
    // cluster counts, the one symbol here that does take part in collision, lose their spot to
    // an invisible pin wherever a layer reorder puts a pin layer above them.
    const defaultIcon = {
      'icon-image': ['get', 'icon'],
      'icon-size': 0.6,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true
    };
    // What both unlit layers below are drawn with. See `UNLIT_MARKER_COLOR` for why a dot.
    const unlitDot = {
      'circle-radius': UNLIT_DOT_RADIUS,
      'circle-color': UNLIT_MARKER_COLOR,
      'circle-stroke-width': 0
    };

    this.map.addLayer(
      {
        id: this.layerDim,
        type: 'circle',
        source: this.source.name,
        paint: {
          ...unlitDot,
          // Nothing is unlit until a focus says so, and `MapFocusService` writes the tier onto
          // this baseline. It must therefore rest at 0, or every stop would carry a dot.
          'circle-opacity': 0
        }
      },
      under
    );

    // The stops of every route a hard filter left out. They are unlit by definition, so they are
    // drawn exactly as an unlit stop is drawn anywhere else, and the focus leaves them alone.
    this.map.addLayer(
      {
        id: this.layerUnselected,
        type: 'circle',
        source: this.source.name,
        filter: ['any', false],
        layout: {
          visibility: 'none'
        },
        paint: {
          ...unlitDot,
          'circle-opacity': UNLIT_DOT_OPACITY
        }
      },
      under
    );

    this.map.addLayer({
      id: this.layerSelected,
      type: 'symbol',
      source: this.source.name,
      filter: ['any', false],
      layout: {
        visibility: 'none',
        ...defaultIcon
      }
    });

    // Visible in both modes: grouped, it is the only layer that can draw a focused route's own
    // stops, because a stop inside a cluster is drawn by no other layer and a focus takes the
    // clusters off the map. `MapFocusService` holds it at zero for every stop no focus has
    // named, so the grouped view looks exactly as it did until one does.
    this.map.addLayer({
      id: this.layerDefault,
      type: 'symbol',
      source: this.source.name,
      layout: {
        visibility: 'visible',
        ...defaultIcon
      }
    });

    this.map.addLayer({
      id: this.layerInvisible,
      type: 'circle',
      source: this.source.name,
      layout: {
        visibility: 'none'
      },
      paint: {
        'circle-radius': 20,
        'circle-opacity': 0
      }
    });

    this.map.addLayer({
      id: this.layerUnclustered,
      type: 'symbol',
      source: this.clusterSource.name,
      filter: ['!', ['has', 'point_count']],
      layout: {
        visibility: 'visible',
        ...defaultIcon
      }
    });

    // Generate grouped icon on demand
    IconMapGalia.ensureIconExists(this.map, 'grouped', '#4e4c4c');

    this.map.addLayer({
      id: this.layerCluster,
      type: 'symbol',
      source: this.clusterSource.name,
      filter: ['has', 'point_count'],
      layout: {
        visibility: 'visible',
        'icon-image': 'grouped-4e4c4c',
        'icon-size': 1,
        'icon-allow-overlap': true
      }
    });

    this.map.addLayer({
      id: this.layerClusterCount,
      type: 'symbol',
      source: this.clusterSource.name,
      filter: ['has', 'point_count'],
      layout: {
        visibility: 'visible',
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 18,
        'text-allow-overlap': false
      },
      paint: {
        'text-color': '#000000'
      }
    });

    this.map.addLayer({
      id: this.layerUnclusteredSpiderfy,
      type: 'symbol',
      source: this.spiderfySource.name,
      filter: ['!', ['has', 'point_count']],
      layout: {
        visibility: 'none',
        ...defaultIcon,
        'icon-offset': ['get', 'icon-offset']
      },
      paint: {
        'icon-opacity': 0
      }
    });

    this.map.addLayer({
      id: this.layerUnclusteredInvisible,
      type: 'circle',
      source: this.clusterSource.name,
      filter: ['!', ['has', 'point_count']],
      layout: {
        visibility: 'visible'
      },
      paint: {
        'circle-radius': 20,
        'circle-opacity': 0
      }
    });

    // The stop numbers, drawn above every pin. Which stops actually show one is decided by
    // `MapFocusService` through feature state, never here: this layer holds every stop the map
    // has, and only the route on display is numbered.
    //
    // It draws the unclustered source in both modes, for the reason `markers.dim` does: that
    // source is the only one holding every stop, including the ones a cluster has swallowed.
    // Drawn from the clustered source instead, a number could only appear for a stop that
    // happened to stand alone — which is why hovering a route used to number none of its stops
    // while a hard filter, whose ungrouped view fans everything apart, numbered them all.
    //
    // It takes no part in collision. A label sitting at zero opacity is still *placed*, so a
    // colliding one would go on denying its icon a spot while showing nothing. What used to be
    // the cost of that — numbers piling onto one another where a route stops twice at one site —
    // is settled upstream instead: `sequenceLabels` merges those into a single label.
    const sequenceLabel = {
      'text-field': ['get', 'sequence'],
      'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
      'text-size': SEQUENCE_TEXT_SIZE,
      // Anchored by its left edge rather than centred, which is what keeps a label off the pin
      // it belongs to whatever it says: centred, the offset positions the middle of the label,
      // so every character past the first grows back over the icon — one digit clears it, two
      // graze it, and a merged `1 · 3` lands on it squarely.
      'text-anchor': 'left',
      'text-offset': [SEQUENCE_TEXT_OFFSET_EM, 0],
      'text-allow-overlap': true,
      'text-ignore-placement': true
    };

    // Dark ink in a white halo, which is what separates the digits from the lit route line they
    // annotate, from the land fill, and from the basemap's own street labels.
    //
    // Not the stop's own colour: a number is only ever drawn while a single route is displayed
    // (`VisualFocusService.numberedActionUids`), so every number on screen already belongs to
    // the same route — colour would encode nothing and cost contrast on the lighter palette
    // entries. `MapFocusService` drives `text-opacity` on these layers and nothing else, so
    // these values survive every focus repaint.
    const sequencePaint = {
      'text-color': '#18181b',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.5,
      'text-halo-blur': 0.5
    };

    this.map.addLayer({
      id: this.layerSequence,
      type: 'symbol',
      source: this.source.name,
      layout: { visibility: 'visible', ...sequenceLabel },
      paint: sequencePaint
    });
  }

  private initSource() {
    this.map.addSource(this.clusterSource.name, {
      type: 'geojson',
      data: this.dataMarkers,
      promoteId: 'id',
      maxzoom: this.clusterMaxZoom,
      cluster: true,
      clusterMaxZoom: this.clusterMaxZoom,
      clusterRadius: 0.1
    });

    this.map.addSource(this.source.name, {
      type: 'geojson',
      data: this.dataMarkers,
      promoteId: 'id',
      cluster: false
    });

    this.map.addSource(this.spiderfySource.name, {
      type: 'geojson',
      promoteId: 'id',
      data: {
        type: 'FeatureCollection',
        features: []
      },
      cluster: false
    });
  }
}

/** Decimal places at which two coordinates count as the same point: ~0.1 m. */
const IDENTICAL_COORD_PRECISION = 6;
/**
 * How far apart two stops of one route can be and still count as standing on one another for
 * labelling.
 *
 * Real data puts two stops of one route a few metres apart at *different* sites, which is one
 * pixel at any zoom worth reading, so exact equality never catches those. Measured as a distance
 * rather than by rounding both coordinates into a grid: a grid of this size still cuts between
 * two stops 2 m apart whenever they straddle a cell edge, which is what used to split a depot's
 * label in two and draw the halves on top of each other.
 */
const COINCIDENT_LABEL_METRES = 11;
/** Metres per degree, as the offset pass measures them. */
const METRES_PER_DEGREE_LNG = 111319;
const METRES_PER_DEGREE_LAT = 110574;
/**
 * Between two numbers sharing a label. A middle dot sits on the digits' optical centre, where a
 * comma drops below the baseline and reads as grit at this text size.
 */
const SEQUENCE_LABEL_SEPARATOR = ' · ';
/** A stop pin's on-screen half-width: a 50px icon drawn at `icon-size` 0.6. */
const PIN_RADIUS_PX = 15;
const SEQUENCE_TEXT_SIZE = 14;
/** Clear air between the pin and the first digit. */
const SEQUENCE_GAP_PX = 3;
/**
 * How far a number's left edge sits from its stop, in ems — the unit `text-offset` is measured
 * in, hence derived from the text size rather than written as a constant of its own: a raw
 * multiple would silently stop clearing the pin the moment the size is edited.
 */
const SEQUENCE_TEXT_OFFSET_EM = (PIN_RADIUS_PX + SEQUENCE_GAP_PX) / SEQUENCE_TEXT_SIZE;

function coordKey(coords: GeoLocation, precision: number): string {
  const factor = Math.pow(10, precision);
  return `${Math.round(coords.lng * factor) / factor},${Math.round(coords.lat * factor) / factor}`;
}

/**
 * The label each stop's number is drawn as: its own number, the numbers of every stop standing
 * with it joined together, or nothing at all for the stops the joined label speaks for.
 *
 * A route that stops twice at one site — a depot opened and closed, a pickup and its delivery —
 * puts two numbers on one pixel, and the label layer draws both because it must not take part in
 * collision (see `initLayer`). Merging is what makes that readable, and it says more than culling
 * one of them would: "1 · 6" tells the planner the site is visited twice, where a lone "1" hides
 * the second visit entirely.
 *
 * Grouped **by route**, so two routes calling at one depot never share a label — a number only
 * ever means a position within its own route.
 *
 * Expects the markers with the coordinates their *sites* have, before the offset pass fans
 * coincident stops apart. A label answers which visits happen at a place, and the fan is a device
 * for separating pins you might want to click rather than a statement about geography — measured
 * on real data it does not even separate them: the ring is shared by every route's stops at that
 * site, so a depot carrying 66 of them puts consecutive positions ~2.4 m apart, a fifth of a
 * pixel at the zoom a whole route is read at.
 */
function sequenceLabels(
  markers: readonly GaliaMarker[],
  sequences: ReadonlyMap<string, number>
): ReadonlyMap<string, string> {
  const routes = new Map<string, GaliaMarker[]>();

  for (const marker of markers) {
    const routeUid = marker.structuralAggregatorIds?.[0];
    if (!routeUid) continue;

    const stops = routes.get(routeUid);
    if (stops) stops.push(marker);
    else routes.set(routeUid, [marker]);
  }

  const labels = new Map<string, string>();

  for (const stops of routes.values()) {
    for (const group of groupByProximity(stops)) {
      const numbers = group
        .map((marker) => sequences.get(marker.uid))
        .filter((sequence): sequence is number => !isNil(sequence))
        .sort((a, b) => a - b);

      for (const marker of group) labels.set(marker.uid, '');
      labels.set(group[0].uid, numbers.join(SEQUENCE_LABEL_SEPARATOR));
    }
  }

  return labels;
}

/**
 * One route's stops, gathered into the places they stand on: a group is every stop within
 * `COINCIDENT_LABEL_METRES` of its first member, which is also the stop the joined label ends up
 * drawn on.
 *
 * Measured against that one anchor rather than against any member already in the group, so a
 * group can never span more than the threshold. Joining on "close to anyone here" reads the same
 * on a depot and grows without limit anywhere else: a street of waste containers each 10 m from
 * the next collapses into one group hundreds of metres long, whose label lists the whole chain on
 * its first stop — wrapped into a block of text by `text-max-width` — and leaves every other stop
 * on the street blank.
 *
 * The bound costs the opposite case: two stops just over the threshold keep a label each, and at
 * a low zoom those still land on top of each other. That is the screen-distance question rather
 * than this one, and no choice here settles it.
 */
function groupByProximity(stops: readonly GaliaMarker[]): GaliaMarker[][] {
  const groups: GaliaMarker[][] = [];

  for (const stop of stops) {
    const home = groups.find(
      (group) => metresBetween(group[0].coords, stop.coords) <= COINCIDENT_LABEL_METRES
    );
    if (home) home.push(stop);
    else groups.push([stop]);
  }

  return groups;
}

/**
 * Flat-earth distance in metres. The distances compared here are metres over a few hundred, so
 * the curvature a great-circle formula would add is far below the threshold it is compared to.
 */
function metresBetween(a: GeoLocation, b: GeoLocation): number {
  const meanLatRad = (((a.lat + b.lat) / 2) * Math.PI) / 180;
  return Math.hypot(
    (a.lng - b.lng) * METRES_PER_DEGREE_LNG * Math.cos(meanLatRad),
    (a.lat - b.lat) * METRES_PER_DEGREE_LAT
  );
}

/**
 * Each stop's position within its own route, counted in the order the markers arrive — the same
 * order the route itself is driven in.
 *
 * A stop is numbered against its **first** aggregator alone. A marker naming several routes
 * would hold a different number in each, and one feature can carry only one label; the number is
 * only ever read while a single route is on the map, which is that stop's own route.
 */
function stopSequences(markers: readonly GaliaMarker[]): ReadonlyMap<string, number> {
  const counters = new Map<string, number>();
  const sequences = new Map<string, number>();

  for (const marker of markers) {
    const routeUid = marker.structuralAggregatorIds?.[0];
    if (!routeUid) continue;

    const next = (counters.get(routeUid) ?? 0) + 1;
    counters.set(routeUid, next);
    sequences.set(marker.uid, next);
  }

  return sequences;
}
